import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * PDF.js-powered inline viewer. Renders every page to a canvas inside
 * a scrollable container, so it works even inside sandboxed iframes
 * where native browser PDF viewers (via <iframe src=blob>) get blocked.
 *
 * Props: url (absolute PDF URL), scale (default 1.35)
 */
export default function PdfJsViewer({ url, scale = 1.35, testid = "pdfjs-viewer" }) {
  const containerRef = useRef(null);
  const [state, setState] = useState({ loading: true, pages: 0, error: null });

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;
    let pdfDoc = null;

    async function render() {
      setState({ loading: true, pages: 0, error: null });
      try {
        // Dynamic import so the large bundle only loads when the viewer opens.
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        // Use the worker we copied into /public/pdf.worker.min.mjs on install.
        pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;

        loadingTask = pdfjs.getDocument({ data: buf });
        pdfDoc = await loadingTask.promise;
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;
        // Use replaceChildren() instead of innerHTML="" — same effect, no XSS risk.
        container.replaceChildren();

        for (let i = 1; i <= pdfDoc.numPages; i++) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(i);
          const viewport = page.getViewport({ scale });
          const dpr = window.devicePixelRatio || 1;

          const canvas = document.createElement("canvas");
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.className = "mx-auto shadow-[0_4px_16px_rgba(26,33,28,0.18)] bg-white rounded";
          canvas.setAttribute("data-testid", `${testid}-page-${i}`);

          const ctx = canvas.getContext("2d");
          ctx.scale(dpr, dpr);

          const wrapper = document.createElement("div");
          wrapper.className = "py-3 flex flex-col items-center";
          const badge = document.createElement("div");
          badge.className = "text-[11px] text-[#E7F1FB] opacity-80 mb-1.5 font-mono";
          badge.textContent = `Page ${i} / ${pdfDoc.numPages}`;
          wrapper.appendChild(badge);
          wrapper.appendChild(canvas);
          container.appendChild(wrapper);

          await page.render({ canvasContext: ctx, viewport }).promise;
        }
        if (!cancelled) setState({ loading: false, pages: pdfDoc.numPages, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, pages: 0, error: e.message || String(e) });
      }
    }

    render();
    return () => {
      cancelled = true;
      try { loadingTask?.destroy?.(); } catch { /* pdf.js worker already stopped */ }
      try { pdfDoc?.destroy?.(); } catch { /* pdf doc already destroyed */ }
    };
  }, [url, scale, testid]);

  return (
    <div className="w-full h-full flex flex-col relative bg-[#2a3630]" data-testid={testid}>
      {state.loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#f4f6f1] gap-2 z-10">
          <Loader2 className="w-6 h-6 animate-spin" />
          <div className="text-[13px]">Rendering PDF…</div>
        </div>
      )}
      {state.error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#fbeae5] gap-2 px-6 text-center z-10">
          <AlertTriangle className="w-6 h-6" />
          <div className="font-semibold">Could not render this document.</div>
          <div className="text-[13px] opacity-80">{state.error}</div>
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-4 py-2"
        data-testid={`${testid}-pages`}
      />
    </div>
  );
}
